import React, { useState } from "react"
import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select"
import { AdminStatsCard } from "@/components/academician/admin-stats-card"
import { CohortProgressChart } from "@/components/academician/cohort-progress-chart"
import { RecentSubmissionsList } from "@/components/academician/recent-submissions-list"
import { FoundersAttentionList } from "@/components/academician/founders-attention-list"
import { AdminQuickActions } from "@/components/academician/admin-quick-actions"
import { RecentEmailLogs } from "@/components/academician/recent-email-logs"
import {
  UsersIcon,
  ActivityIcon,
  TrendingUpIcon,
  AlertCircleIcon
} from "lucide-react"

interface Submission {
  id: string
  founderName: string
  founderAvatar?: string
  activity: string
  type: "module" | "interview" | "assignment"
  timestamp: string
  status?: "completed" | "pending" | "reviewed"
}

interface FounderAttention {
  id: string
  name: string
  avatar?: string
  daysSinceLogin: number
  lastActivity: string
  progress: number
}

interface EmailLogEntry {
  id: string
  mailerClass: string
  mailerAction: string
  subject: string
  toAddress: string
  status: "sent" | "failed"
  sentAt: string
}

interface AdminDashboardPageProps {
  cohorts?: Array<{ id: string; name: string }>
  selectedCohortId?: string
  onCohortChange?: (cohortId: string) => void
  stats?: {
    totalFounders: number
    activePercentage: number
    activeCount: number
    avgProgress: number
    pendingReviews: number
  }
  recentSubmissions?: Submission[]
  attentionFounders?: FounderAttention[]
  progressDistribution?: Array<{ range: string; founders: number }>
  recentEmailLogs?: EmailLogEntry[]
  className?: string
}

export function AdminDashboardPage({
  cohorts = [],
  selectedCohortId,
  onCohortChange,
  stats = { totalFounders: 0, activePercentage: 0, activeCount: 0, avgProgress: 0, pendingReviews: 0 },
  recentSubmissions = [],
  attentionFounders = [],
  progressDistribution = [],
  recentEmailLogs = [],
  className
}: AdminDashboardPageProps) {
  const [currentCohort, setCurrentCohort] = useState(
    selectedCohortId || cohorts[0]?.id || ""
  )

  const handleCohortChange = (cohortId: string) => {
    setCurrentCohort(cohortId)
    if (onCohortChange) {
      onCohortChange(cohortId)
    }
    // Navigate to server with cohort filter
    window.location.href = `/admin/dashboard?cohort_id=${cohortId}`
  }

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Admin Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor and manage your cohorts
            </p>
          </div>

          {/* Cohort Selector */}
          {cohorts.length > 1 && (
            <div className="w-64">
              <Select value={currentCohort} onValueChange={handleCohortChange}>
                <SelectTrigger className="bg-card border-border">
                  <SelectValue placeholder="Select cohort" />
                </SelectTrigger>
                <SelectContent>
                  {cohorts.map((cohort) => (
                    <SelectItem key={cohort.id} value={cohort.id}>
                      {cohort.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <AdminStatsCard
            title="Total Founders"
            value={stats.totalFounders}
            subtitle="enrolled"
            icon={UsersIcon}
          />

          <AdminStatsCard
            title="Active This Week"
            value={`${stats.activePercentage}%`}
            subtitle={`${stats.activeCount} of ${stats.totalFounders}`}
            icon={ActivityIcon}
          />

          <AdminStatsCard
            title="Avg Module Progress"
            value={`${stats.avgProgress}%`}
            subtitle="across all founders"
            icon={TrendingUpIcon}
          />

          <AdminStatsCard
            title="Inactive Founders"
            value={stats.pendingReviews}
            subtitle="no activity in 14 days"
            icon={AlertCircleIcon}
          />
        </div>

        {/* Main Content - 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cohort Progress Overview */}
            <div className="bg-card border border-border rounded-lg p-6">
              <CohortProgressChart data={progressDistribution} cohortId={currentCohort} />
            </div>

            {/* Recent Submissions */}
            <div className="bg-card border border-border rounded-lg p-6">
              <RecentSubmissionsList submissions={recentSubmissions} viewAllHref="/admin/submissions" />
            </div>
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-6">
            {/* Recent Emails Sent */}
            <div className="bg-card border border-border rounded-lg p-6">
              <RecentEmailLogs emailLogs={recentEmailLogs} viewAllHref="/admin/email_logs" />
            </div>

            {/* Founders Needing Attention */}
            <div className="bg-card border border-border rounded-lg p-6">
              <FoundersAttentionList founders={attentionFounders} />
            </div>

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg p-6">
              <AdminQuickActions />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}